import _ from "lodash";
import {
  assessmentToolParentNodeFragment,
  mypObjectiveRubricDescriptorFragmnet,
  mypCriteriaWithDescriptorFragmnet,
  aiTutorFragment,
  organizationFragment,
} from "./AssessmentToolFragment";
import { getAssessmentQuery } from "Assessments/modules/AssessmentQuery";
import { getUnitPlanQuery } from "IBPlanner/modules/IBQuery";
import {
  getAssessmentQuestionsQuery,
  getAssessmentQuestionsQueryV2,
} from "Worksheet/modules/WorksheetQueries";
import { getAssessmentQuestionsQueryVariables } from "Worksheet/modules/WorksheetUtils";
import { questionFragments } from "Worksheet/modules/WorksheetFragments";
import client from "lib/apolloClient/client";

import {
  getOrganizationDpAssessmentRubricQuery,
  getDpAssessmentRubricQuery,
  getOrganizationScoreRubricsQuery,
  getOrganizationStandardRubricsQuery,
} from "./AssessmentToolQuery";
import { isWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";

const fragmentNameMapping = {
  UNIT_PLAN: {
    typeName: "UnitPlan",
    fragment: assessmentToolParentNodeFragment.unitPlan,
    fragmentName: "unitPlanNodeItem",
    query: getUnitPlanQuery,
  },
  ASSESSMENT: {
    typeName: "Assessment",
    fragment: assessmentToolParentNodeFragment.assessment,
    fragmentName: "assessmentNodeItem",
    query: getAssessmentQuery,
  },
  QUESTION: {
    typeName: "Question",
    fragment: questionFragments.question,
    fragmentName: "questionItem",
    query: getAssessmentQuestionsQuery,
  },
  QUESTION_V2: {
    typeName: "Question",
    fragment: questionFragments.question,
    fragmentName: "questionItem",
    query: getAssessmentQuestionsQueryV2,
  },
};

const assessmentToolElementFragmentNameMapping = {
  MYP_OBJECTIVE_DESCRIPTOR: {
    typeName: "MypObjectiveRubricDescriptor",
    fragment: mypObjectiveRubricDescriptorFragmnet.mypObjectiveRubricDescriptor,
    fragmentName: "mypObjectiveRubricDescriptorItem",
  },
};

const getQueryVariablesByType = ({
  id,
  assessmentId,
  type,
  academicYearId,
}) => {
  const _variables = !_.isEmpty(id) ? { id } : {};

  switch (type) {
    case "UNIT_PLAN": {
      return {
        ..._variables,
        collaboratorsFilter: {
          userAcademicYearId: academicYearId,
        },
        exceptionalCollaboratorsFilter: {
          userAcademicYearId: academicYearId,
        },
      };
    }

    case "ASSESSMENT": {
      return {
        ..._variables,
        collaboratorsFilter: {
          userAcademicYearId: academicYearId,
        },
      };
    }

    case "QUESTION": {
      return getAssessmentQuestionsQueryVariables({
        parentId: assessmentId,
        parentType: "ASSESSMENT",
      });
    }
  }

  return _variables;
};

export const writeAssessmentToolOfParentInCache = ({
  id,
  data,
  type,
  academicYearId,
}) => {
  switch (type) {
    case "QUESTION": {
      const isWorksheetQueriesV2 = isWorksheetQueriesV2Enabled();

      const variables = getAssessmentQuestionsQueryVariables({
        parentId: data.id,
        parentType: "ASSESSMENT",
      });

      try {
        client.writeQuery({
          query: isWorksheetQueriesV2
            ? getAssessmentQuestionsQueryV2
            : getAssessmentQuestionsQuery,
          variables,
          data: { node: data },
        });
        return;
      } catch (e) {
        console.error(e);
        return {};
      }
    }
    default:
      try {
        var queryData = {};
        const typeName = fragmentNameMapping[type].typeName;
        const fragment = fragmentNameMapping[type].fragment;
        const fragmentName = fragmentNameMapping[type].fragmentName;

        // do not need id in variables here
        const variables = getQueryVariablesByType({ type, academicYearId });

        queryData = client.writeFragment({
          id: `${typeName}:${id}`,
          fragment: fragment,
          fragmentName: fragmentName,
          data: data,
          variables,
        });
      } catch (e) {
        console.error(e);
        return {};
      }
      if (queryData != null) return queryData;
      else return {};
  }
};

export const getAssessmentToolOfParentFromCache = ({
  id,
  type,
  assessmentId,
  academicYearId,
}) => {
  const isWorksheetQueriesV2 = isWorksheetQueriesV2Enabled();

  let queryData = {};
  const query =
    isWorksheetQueriesV2 && _.isEqual(type, "QUESTION")
      ? fragmentNameMapping["QUESTION_V2"].query
      : fragmentNameMapping[type].query;

  try {
    queryData = client.readQuery({
      query: query,
      variables: getQueryVariablesByType({
        id,
        type,
        academicYearId,
        assessmentId,
      }),
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getCriteriaWithDescriptorFromCache = ({ id }) => {
  let queryData = {};

  try {
    queryData = client.readFragment({
      id: `PlannerElementNode:${id}`,
      fragment: mypCriteriaWithDescriptorFragmnet.mypCriteriaWithDescriptor,
      fragmentName: "mypCriteriaWithDescriptorItem",
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

/*
  Handle all types of Assessment Tool Elements read cache
  E.g - MYP_OBJECTIVE_DESCRIPTOR
*/
export const getAssessmentToolElementFromCache = ({ id, type }) => {
  let queryData = {};
  try {
    const {
      typeName,
      fragment,
      fragmentName,
    } = assessmentToolElementFragmentNameMapping[type];
    queryData = client.readFragment({
      id: `${typeName}:${id}`,
      fragment,
      fragmentName,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

/*
  Handle all types of Assessment Tool Elements write cache
  E.g - MYP_OBJECTIVE_DESCRIPTOR
*/
export const writeAssessmentToolElementInCache = ({ id, data, type }) => {
  let queryData = {};
  try {
    const {
      typeName,
      fragment,
      fragmentName,
    } = assessmentToolElementFragmentNameMapping[type];

    queryData = client.writeFragment({
      id: `${typeName}:${id}`,
      fragment,
      fragmentName,
      data,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getOrganizationDpAssessmentRubricFromCache = ({ id, filters }) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getOrganizationDpAssessmentRubricQuery,
      variables: {
        id,
        filters,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getDpAssessmentRubricFromCache = variables => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getDpAssessmentRubricQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

const aiTutorFragmentMapping = {
  CRITERIA: {
    typeName: "AnalysisCriteria",
    fragment: "criteria",
    fragmentName: "analysisCriteriaItem",
  },
  INDICATOR: {
    typeName: "AnalysisIndicator",
    fragment: "indicator",
    fragmentName: "analysisIndicatorItem",
  },
};

export const writeAnalysisElementInCache = ({ id, type, data }) => {
  let queryData = {};
  const typeName = aiTutorFragmentMapping[type].typeName;
  const fragment = aiTutorFragmentMapping[type].fragment;
  const fragmentName = aiTutorFragmentMapping[type].fragmentName;

  try {
    queryData = client.writeFragment({
      id: `${typeName}:${id}`,
      fragment: aiTutorFragment[fragment],
      fragmentName,
      data,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};

export const getOrganizationScoreRubricsFromCache = ({ id, filters }) => {
  try {
    const queryData = client.readQuery({
      query: getOrganizationScoreRubricsQuery,
      variables: { id, filters },
    });

    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const getOrganizationStandardRubricsFromCache = ({ id, filters }) => {
  try {
    const queryData = client.readQuery({
      query: getOrganizationStandardRubricsQuery,
      variables: { id, filters },
    });

    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const writeOrganizationStandardRubricsInCache = ({
  id,
  data,
  filters,
}) => {
  try {
    const queryData = client.writeFragment({
      id: `Organization:${id}`,
      fragment: organizationFragment.standardRubricList,
      fragmentName: "organizationItem",
      data,
      variables: { filters },
    });

    return queryData || {};
  } catch (e) {
    return {};
  }
};

export const writeOrganizationScoreRubricsInCache = ({ id, data, filters }) => {
  try {
    const queryData = client.writeFragment({
      id: `Organization:${id}`,
      fragment: organizationFragment.scoreRubricList,
      fragmentName: "organizationItem",
      data,
      variables: { filters },
    });

    return queryData || {};
  } catch (e) {
    return {};
  }
};
